import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, NextSteps } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import { isEmpty } from "lodash";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import NextStepItem from "@/components/controls/NextStepItem";
import {
  useDeleteNextStepMutation,
  useUpdateNextStepMutation
} from "@/pages/deals/hooks";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import toast from "react-hot-toast";
import Skeleton from "@/components/ui/skeleton";
import { NextStep, NextStepsResponse } from "@/pages/deals/types";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";

interface Props {
  dealId: string;
  allNextSteps: NextStep[];
  isLoading: boolean;
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setHasNextStepsInternal: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<NextStepsResponse, Error>>;
}

const NextStepsSection: React.FC<Props> = ({
  dealId,
  allNextSteps,
  isLoading,
  isExpanded,
  setIsExpanded,
  setHasNextStepsInternal,
  refetch
}) => {
  const { t } = useTranslation();
  const { isDropdownClosing } = useDropdownClosing();

  const updateNextStep = useUpdateNextStepMutation();
  const deleteNextStep = useDeleteNextStepMutation();

  const [deleteNextStepModalState, setDeleteNextStepModalState] = useState<{
    isOpen: boolean;
    nextStepId: string | null;
  }>({
    isOpen: false,
    nextStepId: null
  });

  const nextSteps = allNextSteps.filter((step) => !step.isDeleted).slice(0, 1);

  const handleUpdateStep = async (nextStepId: string, newText?: string) => {
    let trimmedValue = newText;
    if (newText !== undefined) {
      trimmedValue = newText?.trim();
      if (isEmpty(trimmedValue)) {
        setDeleteNextStepModalState({
          isOpen: true,
          nextStepId: nextStepId
        });
        return;
      }
    }
    updateNextStep.mutate(
      {
        pathParams: { dealId, nextStepId },
        data: {
          ...(newText !== undefined && { description: trimmedValue })
        }
      },
      {
        onSuccess: () => refetch()
      }
    );
  };

  const handleDeleteStep = () => {
    if (!deleteNextStepModalState.nextStepId) return;
    deleteNextStep.mutate(
      {
        pathParams: {
          dealId,
          nextStepId: deleteNextStepModalState.nextStepId
        }
      },
      {
        onSuccess: () => {
          setDeleteNextStepModalState({
            isOpen: false,
            nextStepId: null
          });
          refetch();
          setHasNextStepsInternal(false);
          toast.success(t("deals.nextSteps.stepDeleted"), {
            duration: 5000,
            icon: null
          });
        }
      }
    );
  };

  const handleNextStepDelete = (nextStepId: string) => {
    setDeleteNextStepModalState({
      isOpen: true,
      nextStepId: nextStepId
    });
  };

  const setIsDeleteNextStepModalOpen = (
    value: boolean | ((prev: boolean) => boolean)
  ) => {
    setDeleteNextStepModalState((prevState) => ({
      ...prevState,
      isOpen: typeof value === "function" ? value(prevState.isOpen) : value
    }));
  };

  return (
    <>
      <div className="flex flex-col w-full">
        <Button
          variant="secondaryNoBg"
          className="border-none w-fit group/nextsteps"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          <div className="relative transition-all duration-200 ease-in-out">
            <IconContainer
              size="5"
              color="tertiary"
              className={cn(
                "transition-all duration-200 ease-in-out [&>svg]:size-full",
                isExpanded
                  ? "opacity-0 scale-95"
                  : "opacity-100 scale-100 group-hover/nextsteps:opacity-0 group-hover/nextsteps:scale-95"
              )}
            >
              <NextSteps />
            </IconContainer>
            <IconContainer
              size="5"
              color="primary"
              className={cn(
                "absolute inset-0 transition-all duration-200 ease-in-out [&>svg]:size-full",
                isExpanded
                  ? "opacity-0 scale-95"
                  : "opacity-0 scale-95 group-hover/nextsteps:opacity-100 group-hover/nextsteps:scale-100"
              )}
            >
              <ChevronRight />
            </IconContainer>
            <IconContainer
              size="5"
              color="primary"
              className={cn(
                "absolute inset-0 transition-all duration-200 ease-in-out [&>svg]:size-full",
                isExpanded ? "opacity-100 scale-100" : "opacity-0 scale-95"
              )}
            >
              <ChevronDown />
            </IconContainer>
          </div>
          <Text variant="bodySecondary">{t("deals.nextSteps.title")}</Text>
        </Button>
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-300 ease-in-out",
            isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          )}
        >
          <div className="overflow-hidden">
            {isLoading ? (
              <div className="flex flex-col gap-2 p-2 w-full">
                <Skeleton className="h-[15rem] w-full" />
                <Skeleton className="h-5 w-full" />
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                {nextSteps.map((nextStep) => (
                  <NextStepItem
                    key={nextStep.id}
                    nextStep={nextStep}
                    allNextSteps={allNextSteps}
                    onUpdate={handleUpdateStep}
                    onDelete={handleNextStepDelete}
                    showDropdown
                    className="bg-surface-elevation2 p-2 rounded-lg"
                    dateClassName="justify-start !text-tertiary"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={deleteNextStepModalState.isOpen && !isDropdownClosing}
        setIsOpen={setIsDeleteNextStepModalOpen}
        title={t("deals.nextSteps.deletionModal.title")}
        confirmationText={t("deals.nextSteps.deletionModal.confirmation")}
        handleSubmit={handleDeleteStep}
      />
    </>
  );
};

export default NextStepsSection;
